import { useEffect } from 'react';
import { useNavigate } from 'js/hooks/navigate';
import { getCleanUrl } from 'js/utils/url.util';
import { useLocation, useParams } from 'react-router-dom';
import { path, propEq, find, omit, prop, pick } from 'ramda';
import { usePortletConfig, withPortlet } from 'js/components/system/Portlet';
import { componentService } from 'js/services/component.service';
import { useAppSelector, usePageSelector, useUserSelector } from 'js/store/hooks';
import { branch, compose, renderNothing, withProps, renderComponent, mapProps } from 'recompose';

import Skeleton from './Skeleton';
import SkeletonWithError from './Skeleton/SkeletonWIthError';
import DetailsBare from './Details';
import { statuses } from '../constants';
import withLeaderBoardDetails from './withDetails';
import { withEmptyState } from '../common/EmptyState';
import { DISPLAY_PLAYER, CASINO_LEADER_BOARD_DETAILS } from './constants';

export const NavigateHome = () => {
    const navigateWithReplace = useNavigate({ replace: true });
    const rootPage = useAppSelector(prop('rootPage'));

    useEffect(() => {
        navigateWithReplace(rootPage);
    }, [rootPage, navigateWithReplace]);

    return null;
};

export const withLeaderBoardId = withProps(
    () => {
        const { pathname } = useLocation();
        const popupBackgroundPagePath = usePageSelector(prop('popupBackgroundPagePath'));
        const leaderBoardPage = '/casino-leaderboard';
        const { leaderboardId } = useParams();
        const cleanUrl = getCleanUrl(popupBackgroundPagePath || pathname);
        const isLbPage = cleanUrl.startsWith(leaderBoardPage);

        if (leaderboardId) {
            return {
                leaderboardId,
                isNotLeaderBoardPage: false
            };
        }

        return {
            isNotLeaderBoardPage: !isLbPage,
            leaderboardId: isLbPage ? cleanUrl.split('/').pop() : null
        };
    }
);

export const onlyForPlayers = compose(
    withProps(() => useUserSelector(pick(['role']))),
    branch(
        propEq('role', 'admin'),
        renderNothing
    ),
    mapProps(omit(['role']))
);

export const withTopCompetitorsCount = withProps(
    () => {
        const { competitorsConfig, topCompetitorsCount, belowCompetitorsCount,
            aboveCompetitorsCount, doNotShowLastCompetitor } = usePortletConfig();

        if (competitorsConfig) {
            return {
                topCompetitorsCount,
                belowCompetitorsCount,
                aboveCompetitorsCount,
                doNotShowLastCompetitor
            };
        }
    }
);

export const withNetworkId = withProps(
    () => {
        const { displayPlayer, networkId, networkType } = useAppSelector(prop('leaderboard'));
        const playerNetworkData = useUserSelector(path(['userDetails', 'playerNetworkData']));
        const findNetworkData = data => data && find(propEq(networkType), data);

        switch (displayPlayer) {
            case DISPLAY_PLAYER.nicknameByNetworkType: {
                const networkData = findNetworkData(playerNetworkData);

                return networkData && {
                    networkId: networkData.networkId
                };
            }
            case DISPLAY_PLAYER.username: {
                return {
                    isToUseUsername: true
                };
            }
            case DISPLAY_PLAYER.nicknameByNetworkId:
                return {
                    networkId
                };
            default: break;
        }
    }
);

export const Details = compose(
    onlyForPlayers,
    withLeaderBoardId,
    branch(
        ({ isNotLeaderBoardPage }) => isNotLeaderBoardPage,
        renderNothing
    ),
    branch(
        ({ leaderboardId }) => !leaderboardId,
        renderComponent(NavigateHome)
    ),
    withPortlet({ forLoggedIn: true }),
    withTopCompetitorsCount,
    withNetworkId,
    withLeaderBoardDetails,
    branch(
        ({ error }) => {
            return error;
        },
        renderComponent(SkeletonWithError)
    ),
    branch(
        ({ details, error }) => !details && !error,
        renderComponent(Skeleton)
    ),
    withEmptyState,
    withProps(
        ({ details }) => {
            const {
                leaderboardRules = [],
                topPrizes = [],
                commonPrize,
                status
            } = details;

            const {
                displayPrizes,
                displayCompetitorsTable: displayTable,
                competitorsTablePositioning,
                displayCountdown,
                setNicknamePage,
                showBackButton,
                backButtonUrl,
                useTournament,
                useRounds
            } = usePortletConfig();

            return {
                competitorsTablePositioning,
                displayTable,
                displayCountdown: displayCountdown &&
                    status !== statuses.COMPLETED &&
                    status !== statuses.PENDING_REVIEW,
                displayGames: Boolean(leaderboardRules.length
                    && leaderboardRules.find((rule) => rule.gameRoundRuleInfo.games.length > 0)),
                displayPrizes: Boolean(topPrizes.length || commonPrize) && displayPrizes,
                setNicknamePage,
                showBackButton,
                backButtonUrl,
                useTournament,
                useRounds
            };
        }
    )
)(DetailsBare);

componentService.registerPageComponents({ [CASINO_LEADER_BOARD_DETAILS]: Details });
